import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Package, 
  TrendingUp, 
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  ArrowUpCircle,
  ArrowDownCircle,
  RotateCcw,
  FileText,
  Calendar,
  User
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const InventoryPage = () => {
  const { company, person } = useAuth();
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  const [activeTab, setActiveTab] = useState('stock'); // 'stock' ou 'movements'
  const [showNewMovementModal, setShowNewMovementModal] = useState(false);

  const [newMovement, setNewMovement] = useState({
    product_id: '',
    type: 'in', // 'in' ou 'out'
    quantity: '',
    reason: '',
    notes: '',
    cost_per_unit: ''
  });

  const movementTypes = [
    { value: 'in', label: 'Entrada', icon: ArrowUpCircle, color: 'text-green-600' },
    { value: 'out', label: 'Saída', icon: ArrowDownCircle, color: 'text-red-600' },
    { value: 'adjustment', label: 'Ajuste', icon: RotateCcw, color: 'text-blue-600' }
  ];

  const movementReasons = {
    in: [
      'Compra',
      'Doação',
      'Devolução',
      'Transferência',
      'Produção',
      'Ajuste de inventário'
    ],
    out: [
      'Venda',
      'Consumo interno',
      'Quebra/Perda',
      'Vencimento',
      'Transferência',
      'Ajuste de inventário'
    ],
    adjustment: [
      'Contagem de estoque',
      'Correção de erro',
      'Inventário físico'
    ]
  };

  useEffect(() => {
    if (company?.id) {
      loadData();
    }
  }, [company]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Carregar produtos com informações de estoque
      const productsData = await dataHelpers.getProducts(company.id);
      setProducts(productsData || []);        // Carregar movimentações de estoque
      const movementsData = await dataHelpers.getInventoryMovements(company.id);
      console.log('Movements data loaded:', movementsData);
      console.log('Number of movements:', movementsData?.length || 0);
      if (movementsData && movementsData.length > 0) {
        console.log('First movement sample:', movementsData[0]);
        console.log('Cost per unit in first movement:', movementsData[0]?.cost_per_unit);
      }
      setMovements(movementsData || []);
      
    } catch (error) {
      console.error('Error loading inventory data:', error);
      toast.error('Erro ao carregar dados do estoque');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMovement = async () => {
    try {
      if (!newMovement.product_id || !newMovement.quantity || !newMovement.reason) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }      const product = products.find(p => p.id === newMovement.product_id);
      if (!product) {
        toast.error('Produto não encontrado');
        return;
      }

      const quantity = parseInt(newMovement.quantity);
      if (quantity <= 0) {
        toast.error('Quantidade deve ser maior que zero');
        return;
      }

      // Verificar se há estoque suficiente para saída
      if (newMovement.type === 'out' && quantity > (product.stock_quantity || 0)) {
        toast.error('Quantidade insuficiente em estoque');
        return;
      }      const movementData = {
        product_id: newMovement.product_id, // Manter como UUID string
        type: newMovement.type,
        quantity: quantity,
        reason: newMovement.reason,
        notes: newMovement.notes || null,
        cost_per_unit: newMovement.cost_per_unit ? parseFloat(newMovement.cost_per_unit) : null,
        created_by: person.id,
        previous_quantity: product.stock_quantity || 0
      };      console.log('Creating movement with data:', movementData);
      console.log('Cost per unit being sent:', movementData.cost_per_unit);
      const result = await dataHelpers.createInventoryMovement(company.id, movementData);
      console.log('Movement created:', result);
      console.log('Cost per unit in result:', result?.cost_per_unit);
      toast.success('Movimentação registrada com sucesso!');
      
      setShowNewMovementModal(false);
      setNewMovement({
        product_id: '',
        type: 'in',
        quantity: '',
        reason: '',
        notes: '',
        cost_per_unit: ''
      });
      
      loadData();
    } catch (error) {
      console.error('Error creating movement:', error);
      toast.error('Erro ao registrar movimentação');
    }
  };

  const getStockStatus = (product) => {
    const stock = product.stock_quantity || 0;
    const minStock = product.min_stock || 0;
    
    if (stock === 0) {
      return { status: 'out', label: 'Sem estoque', color: 'bg-red-100 text-red-800', icon: XCircle };
    } else if (stock <= minStock) {
      return { status: 'low', label: 'Estoque baixo', color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle };
    } else {
      return { status: 'ok', label: 'Em estoque', color: 'bg-green-100 text-green-800', icon: CheckCircle };
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    
    const matchesStockFilter = !showLowStock || 
      (product.stock_quantity || 0) <= (product.min_stock || 0);
    
    return matchesSearch && matchesCategory && matchesStockFilter;
  });

  const recentMovements = movements.slice(0, 10);

  const stats = {
    totalProducts: products.length,
    inStock: products.filter(p => (p.stock_quantity || 0) > 0).length,
    lowStock: products.filter(p => (p.stock_quantity || 0) <= (p.min_stock || 0)).length,
    outOfStock: products.filter(p => (p.stock_quantity || 0) === 0).length,
    totalValue: products.reduce((sum, p) => sum + ((p.stock_quantity || 0) * (p.price || 0)), 0)
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }
  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Controle de Estoque</h1>
            <p className="text-gray-600">
              Gerencie seu estoque e movimentações
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Button onClick={() => setShowNewMovementModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nova Movimentação
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total de Produtos</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.totalProducts}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Em Estoque</p>
                <p className="text-2xl font-semibold text-green-600">{stats.inStock}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Estoque Baixo</p>
                <p className="text-2xl font-semibold text-yellow-600">{stats.lowStock}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Sem Estoque</p>
                <p className="text-2xl font-semibold text-red-600">{stats.outOfStock}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Valor Total</p>
                <p className="text-lg font-semibold text-purple-600">{formatCurrency(stats.totalValue)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 bg-gradient-to-r from-gray-100 to-gray-200 p-1.5 rounded-xl shadow-inner w-fit">
          <button
            onClick={() => setActiveTab('stock')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center space-x-2 ${
              activeTab === 'stock'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg transform scale-105'
                : 'text-gray-700 hover:text-blue-600 hover:bg-white/50'
            }`}
          >
            <Package className="w-4 h-4" />
            <span>Estoque</span>
          </button>
          <button
            onClick={() => setActiveTab('movements')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center space-x-2 ${
              activeTab === 'movements'
                ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg transform scale-105'
                : 'text-gray-700 hover:text-green-600 hover:bg-white/50'
            }`}
          >
            <FileText className="w-4 h-4" />
            <span>Movimentações</span>
          </button>
        </div>

        {activeTab === 'stock' ? (
          <>
            {/* Filters */}
            <Card className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar produtos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Todas as categorias</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>

                <label className="flex items-center px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <input
                    type="checkbox"
                    checked={showLowStock}
                    onChange={(e) => setShowLowStock(e.target.checked)}
                    className="rounded border-yellow-300 text-yellow-600 shadow-sm focus:border-yellow-300 focus:ring focus:ring-yellow-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm font-medium text-yellow-800">
                    Apenas estoque baixo
                  </span>
                </label>

                <Button variant="outline" className="justify-center">
                  <Filter className="w-4 h-4 mr-2" />
                  Mais Filtros
                </Button>
              </div>
            </Card>

            {/* Products Stock List */}
            <Card>
              <div className="overflow-hidden">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Produto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Estoque Atual
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Estoque Mínimo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Valor Unitário
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Valor Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredProducts.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center">
                          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500">Nenhum produto encontrado</p>
                        </td>
                      </tr>
                    ) : (
                      filteredProducts.map((product) => {
                        const stockStatus = getStockStatus(product);
                        const StatusIcon = stockStatus.icon;
                        
                        return (
                          <tr key={product.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                {product.image_url && (
                                  <img
                                    className="h-10 w-10 rounded-full object-cover mr-3"
                                    src={product.image_url}
                                    alt={product.name}
                                  />
                                )}
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {product.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {product.category}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm font-medium text-gray-900">
                                {product.stock_quantity || 0}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-900">
                                {product.min_stock || 0}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${stockStatus.color}`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {stockStatus.label}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-900">
                                {formatCurrency(product.price || 0)}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm font-medium text-gray-900">
                                {formatCurrency((product.stock_quantity || 0) * (product.price || 0))}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        ) : (
          /* Movements Tab */
          <Card>
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Movimentações Recentes</h3>
            </div>
            <div className="overflow-hidden">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Data/Hora
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Produto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Tipo
                    </th>                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Quantidade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Valor Unit.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Valor Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Motivo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Usuário
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">                  {recentMovements.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center">
                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">Nenhuma movimentação encontrada</p>
                      </td>
                    </tr>
                  ) : (                    recentMovements.map((movement) => {
                      console.log('Movement data:', movement); // Debug
                      const movementType = movementTypes.find(t => t.value === movement.type);
                      const MovementIcon = movementType?.icon || FileText;
                      
                      return (
                        <tr key={movement.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-900">
                              {formatDate(movement.created_at)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm font-medium text-gray-900">
                              {movement.products?.name || 'Produto não encontrado'}
                            </p>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              movement.type === 'in' ? 'bg-green-100 text-green-800' :
                              movement.type === 'out' ? 'bg-red-100 text-red-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              <MovementIcon className="w-3 h-3 mr-1" />
                              {movementType?.label || movement.type}
                            </span>
                          </td>                          <td className="px-6 py-4">
                            <span className={`text-sm font-medium ${
                              movement.type === 'in' ? 'text-green-600' :
                              movement.type === 'out' ? 'text-red-600' : 'text-blue-600'
                            }`}>
                              {movement.type === 'in' ? '+' : movement.type === 'out' ? '-' : '±'}{movement.quantity}
                            </span>
                          </td>                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-900">
                              {(() => {
                                console.log(`Movement ${movement.id} cost_per_unit:`, movement.cost_per_unit);
                                return formatCurrency(movement.cost_per_unit || 0);
                              })()}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm font-medium text-gray-900">
                              {(() => {
                                const total = (movement.cost_per_unit || 0) * movement.quantity;
                                console.log(`Movement ${movement.id} total calculation:`, movement.cost_per_unit, '*', movement.quantity, '=', total);
                                return formatCurrency(total);
                              })()}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-900">
                              {movement.reason}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-900">
                              {movement.creator?.name || 'Usuário não identificado'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* New Movement Modal */}
      {showNewMovementModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Nova Movimentação de Estoque</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Produto
                </label>
                <select
                  value={newMovement.product_id}
                  onChange={(e) => setNewMovement(prev => ({ ...prev, product_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Selecione um produto</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} (Estoque: {product.stock_quantity || 0})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Movimentação
                </label>
                <select
                  value={newMovement.type}
                  onChange={(e) => setNewMovement(prev => ({ ...prev, type: e.target.value, reason: '' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {movementTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <Input
                label="Quantidade"
                type="number"
                min="1"
                value={newMovement.quantity}
                onChange={(e) => setNewMovement(prev => ({ ...prev, quantity: e.target.value }))}
                placeholder="0"
                required
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo
                </label>
                <select
                  value={newMovement.reason}
                  onChange={(e) => setNewMovement(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Selecione o motivo</option>
                  {movementReasons[newMovement.type]?.map(reason => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </div>
              
              {newMovement.type === 'in' && (
                <Input
                  label="Custo por Unidade (opcional)"
                  type="number"
                  step="0.01"
                  value={newMovement.cost_per_unit}
                  onChange={(e) => setNewMovement(prev => ({ ...prev, cost_per_unit: e.target.value }))}
                  placeholder="0,00"
                />
              )}
              
              <div className={newMovement.type === 'in' ? '' : 'md:col-span-2'}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={newMovement.notes}
                  onChange={(e) => setNewMovement(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Observações sobre a movimentação..."
                />
              </div>
            </div>
            
            <div className="mt-6 flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowNewMovementModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateMovement}
                className="flex-1"
              >
                Registrar Movimentação
              </Button>            </div>
          </div>
        </div>
      )}
      </>
    );
};

export default InventoryPage;